import type {Route} from './+types/root';

import * as Sentry from '@sentry/react';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useEffect} from 'react';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';
import {ToastContainer} from 'react-toastify';

import './i18n/config';
import '@dokia/ui-app/styles';

import {ErrorPage} from './components/pages/ErrorPage';
import {StoreProvider} from './store/useStoreContext';
import {
  AxeptioHead,
  GoogleTagManagerBody,
  GoogleTagManagerHead,
  ProduktlyHead,
  VirtualPageViewTracker,
} from './tracking';
import {TrackingContextSync} from './tracking/TrackingContextSync';
import '~/clients/strapi';

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <StoreProvider>
        <TrackingContextSync />
        <VirtualPageViewTracker />
        <Outlet />
      </StoreProvider>
      <ToastContainer
        autoClose={7000}
        closeOnClick
        draggable
        hideProgressBar={false}
        newestOnTop={false}
        pauseOnHover
        position="bottom-right"
        rtl={false}
      />
    </QueryClientProvider>
  );
}

export function ErrorBoundary({error}: Route.ErrorBoundaryProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return <ErrorPage error={error} />;
}

export function HydrateFallback() {
  return null;
}

export function Layout({children}: {children: React.ReactNode}) {
  return (
    <html lang="fr">
      <head>
        <AxeptioHead />
        <GoogleTagManagerHead />
        <ProduktlyHead />
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <link href="/favicon.svg" rel="icon" type="image/svg+xml" />
        <link href="/site.webmanifest" rel="manifest" />
        <Meta />
        <Links />
      </head>
      <body>
        <GoogleTagManagerBody />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function meta() {
  return [{title: 'dokia.'}];
}
